import { useState } from "react";

export interface StaffFormData {
  name: string;
  email: string;
  password?: string;
  role: string;
  phone: string;
  status: string;
  notes: string;
}

interface StaffFormProps {
  onSubmit: (data: StaffFormData) => void;
  onCancel: () => void;
  initialData?: Partial<StaffFormData>;
  /** When true, the Super Admin role option is shown in the picker. */
  canAssignSuperAdmin?: boolean;
}

const ALL_ROLES = [
  { value: "super_admin",  label: "Super Admin",  description: "Hidden role — full system access, can manage other super admins." },
  { value: "admin",        label: "Admin",        description: "Full operational access; manages staff and configuration." },
  { value: "manager",      label: "Manager",      description: "Event operations, reports, vendors. Cannot manage staff." },
  { value: "receptionist", label: "Receptionist", description: "Front desk: enquiries, quotations, clients, calendar." },
  { value: "staff",        label: "Staff",        description: "Basic access: assigned events, tasks, calendar only." },
];

const ROLE_COLORS: Record<string, string> = {
  super_admin:  "bg-amber-100 text-amber-800 border-amber-300",
  admin:        "bg-amber-50 text-amber-700 border-amber-200",
  manager:      "bg-violet-100 text-violet-700 border-violet-200",
  receptionist: "bg-sky-100 text-sky-700 border-sky-200",
  staff:        "bg-stone-100 text-stone-700 border-stone-200",
};

export default function StaffForm({ onSubmit, onCancel, initialData, canAssignSuperAdmin = false }: StaffFormProps) {
  // Always show super_admin if the user being edited already has it (so super_admin can't accidentally lose access).
  const editingExistingSuperAdmin = initialData?.role === "super_admin";
  const ROLES = ALL_ROLES.filter(r =>
    r.value !== "super_admin" || canAssignSuperAdmin || editingExistingSuperAdmin
  );

  const [formData, setFormData] = useState<StaffFormData>({
    name: initialData?.name || "",
    email: initialData?.email || "",
    password: "",
    role: initialData?.role || "",
    phone: initialData?.phone || "",
    status: initialData?.status || "active",
    notes: initialData?.notes || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof StaffFormData, string>>>({});

  const validate = (): boolean => {
    const next: Partial<Record<keyof StaffFormData, string>> = {};
    if (!formData.name.trim())  next.name = "Name is required";
    if (!formData.email.trim()) next.email = "Email is required";
    if (!initialData && !formData.password?.trim()) next.password = "Password is required";
    if (!formData.role) next.role = "Role is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  const handleChange = (field: keyof StaffFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const inputClass = (field: keyof StaffFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field]
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  const selectedRole = ROLES.find(r => r.value === formData.role);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Full Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Enter full name"
            className={inputClass("name")}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="staff@shalimar.in"
            className={inputClass("email")}
          />
          {errors.email && <p className="text-xs text-rose-500 mt-1">{errors.email}</p>}
        </div>

        {!initialData && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              placeholder="Minimum 8 characters"
              className={inputClass("password")}
            />
            {errors.password && <p className="text-xs text-rose-500 mt-1">{errors.password}</p>}
          </div>
        )}

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Role *</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ROLES.map(r => (
              <button
                key={r.value}
                type="button"
                onClick={() => handleChange("role", r.value)}
                className={`text-left px-3 py-2 rounded-xl border-2 text-sm transition-colors ${
                  formData.role === r.value
                    ? `${ROLE_COLORS[r.value]} border-current`
                    : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
                }`}
              >
                <p className="font-semibold">{r.label}</p>
                <p className="text-[11px] opacity-75 mt-0.5">{r.description}</p>
              </button>
            ))}
          </div>
          {errors.role && <p className="text-xs text-rose-500 mt-1">{errors.role}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Phone Number</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="+91 98765 43210"
            className={inputClass("phone")}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <div className="flex gap-2">
            {["active", "inactive"].map(s => (
              <button
                key={s}
                type="button"
                onClick={() => handleChange("status", s)}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors border ${
                  formData.status === s
                    ? s === "active"
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-stone-100 text-stone-500 border-stone-200"
                    : "bg-stone-50 text-stone-400 border-stone-200 hover:bg-stone-100"
                }`}
              >
                {s === "active" ? "Active" : "Inactive"}
              </button>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Additional information..."
            rows={3}
            className={`${inputClass("notes")} resize-none`}
          />
        </div>
      </div>

      {selectedRole && (
        <div className={`p-3 rounded-xl border text-xs ${ROLE_COLORS[selectedRole.value]}`}>
          <p className="font-semibold">{selectedRole.label}</p>
          <p className="opacity-80 mt-0.5">{selectedRole.description}</p>
          <p className="mt-1 text-[10px] opacity-70">
            Page access for this role can be edited in Settings &rarr; Roles &amp; Access.
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update User" : "Add User"}
        </button>
      </div>
    </form>
  );
}
